import { useEffect, useState } from "react";
import { Form, Input, FormFeedback, Button } from "reactstrap";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";
import { setup2Fa, verify2Fa } from "../../api/auth";
import ResultHandler from "../../components/ResultHandler";
import useCustomValidation from "../dashboard/validationSchema";
import { useResponse } from "../../hooks/useResponse";
import toast from "react-hot-toast";
import useClipboard from "../../hooks/useClipboard";
import Remove2FaModal from "./Remove2FaModal";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const TwoFactorAuth = () => {
  const { twoFactorVerificationSchema } = useCustomValidation();
  const history = useHistory();
  const { copyText } = useClipboard();

  // Setup
  const [qrCodeData, setQrCodeData] = useState(null);

  const {
    setData: getResponse,
    loading: responseLoading,
    error: responseError,
  } = useResponse(setup2Fa, (res) => {
    setQrCodeData(res);
  });

  useEffect(() => getResponse({ type: "add" }), []);

  // Verify
  const { setData, loading, error } = useResponse(verify2Fa, (res) => {
    history.push("/");
    localStorage.setItem("twoFactorEnabled", true);
    toast.success(res.message);
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { code: "" },
    validationSchema: twoFactorVerificationSchema,
    onSubmit: (values) => {
      setData({ type: "add", data: values });
    },
  });

  // remove
  const [show, setShow] = useState(false);

  if (responseError === "Two Factor is already set up!") {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs breadcrumbItem="Two factor authintication" />

          <div className="text-center d-flex justify-content-center align-items-center flex-column gap-4 mt-5">
            <h5 style={{ fontSize: "17px", fontWeight: "600" }}>
              Two factor authintication is already enabled!
            </h5>
            <Button
              onClick={() => setShow(true)}
              color="danger"
              className="fw-bold d-flex gap-2 justify-content-center align-items-center"
            >
              Remove two factor authintication
            </Button>
          </div>

          <Remove2FaModal
            show={show}
            setShow={setShow}
            refetch={() => getResponse({ type: "add" })}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs breadcrumbItem="Two factor authintication" />

        <div className="d-flex justify-content-center align-items-center flex-column mt-3 mb-5">
          <ResultHandler error={responseError} loading={responseLoading} />

          {!responseLoading && qrCodeData && (
            <div
              className="d-flex justify-content-center align-items-center flex-column"
              style={{ width: "500px", maxWidth: "100%" }}
            >
              <div className="w-100">
                <h5
                  style={{
                    fontSize: "17px",
                    fontWeight: "600",
                    borderLeft: "3px solid",
                    paddingLeft: "10px",
                  }}
                >
                  Scan QR Code
                </h5>

                <div className="d-flex justify-content-center align-items-center my-3">
                  <img src={qrCodeData.qrCodeUrl} alt="qr code" />
                </div>

                <p style={{ fontWeight: "600" }}>
                  <p className="mb-0">
                    1 - Open your two-factor authintication app.
                    <a
                      className="ms-2 text-info fw-bold"
                      target="_blank"
                      href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en"
                    >
                      Download App
                    </a>
                  </p>
                  <p className="mb-0">2 - Scan this QR Code. Or use this key</p>
                  <p
                    style={{
                      fontSize: "13px",
                      marginInlineStart: "6px",
                      cursor: "pointer",
                      wordWrap: "break-word",
                    }}
                    onClick={() => copyText(qrCodeData.secret)}
                    className="mb-0"
                  >
                    {qrCodeData.secret}
                    <i class="bx bxs-copy ms-1"></i>
                  </p>
                  <p className="mb-0">
                    3 - Add the generated verification code in the next field.
                  </p>
                  <p className="mb-0">4 - Click Verify code button below.</p>
                </p>
              </div>

              <div className="w-100 mt-2">
                <Form
                  className="form-horizontal"
                  onSubmit={formik.handleSubmit}
                >
                  <div className="mb-3">
                    <h5
                      style={{
                        fontSize: "17px",
                        fontWeight: "600",
                        borderLeft: "3px solid",
                        paddingLeft: "10px",
                        marginBottom: "16px",
                      }}
                    >
                      Enter Verification Code
                    </h5>
                    <Input
                      name="code"
                      className="form-control"
                      type="text"
                      placeholder="Verification code"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.code || ""}
                      invalid={
                        formik.touched.code && formik.errors.code ? true : false
                      }
                    />
                    {formik.touched.code && formik.errors.code ? (
                      <FormFeedback type="invalid">
                        {formik.errors.code}
                      </FormFeedback>
                    ) : null}
                  </div>

                  <p style={{ fontWeight: "600" }}>
                    Enter the code from your two-factor authintication app.
                  </p>

                  <ResultHandler error={error} />

                  <Button
                    color="primary"
                    type="submit"
                    disabled={loading}
                    className="w-100 fw-bold"
                  >
                    {loading ? "Loading..." : "Verify code"}
                  </Button>
                </Form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuth;
